/**
 * Toast — quiet, lower-right notification surface.
 *
 * Replaces browser `alert()` everywhere. Four kinds: info, success, warning, error.
 * Each toast auto-dismisses after 6s (or 10s for errors). Manual dismiss via ✕.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success("Invite sent");
 *   toast.error("Couldn't save", "The server returned 500.");
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { IconX, IconCheck } from "./Icons";

export type ToastKind = "info" | "success" | "warning" | "error";

interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  detail?: string;
  expiresAt: number;
}

interface ToastApi {
  info:    (title: string, detail?: string) => void;
  success: (title: string, detail?: string) => void;
  warning: (title: string, detail?: string) => void;
  error:   (title: string, detail?: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, title: string, detail?: string) => {
      const id = nextId.current++;
      const ttl = kind === "error" ? 10_000 : 6_000;
      const toast: Toast = { id, kind, title, detail, expiresAt: Date.now() + ttl };
      setToasts((prev) => [...prev, toast]);
      setTimeout(() => dismiss(id), ttl);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      info:    (t, d) => push("info", t, d),
      success: (t, d) => push("success", t, d),
      warning: (t, d) => push("warning", t, d),
      error:   (t, d) => push("error", t, d),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== "undefined" && createPortal(
        <ToastStack toasts={toasts} onDismiss={dismiss} />,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

// ─── Presentation ───────────────────────────────────────────────────────────

function ToastStack({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div
      className="toast-stack"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  // Enter animation via data-mounted
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      className={`toast toast-${toast.kind}`}
      data-mounted={mounted ? "true" : "false"}
      role={toast.kind === "error" ? "alert" : "status"}
    >
      <div className="toast-icon" aria-hidden="true">
        {toast.kind === "success" ? <IconCheck size={14} /> : <span className="toast-dot" />}
      </div>
      <div className="toast-copy">
        <div className="toast-title">{toast.title}</div>
        {toast.detail && <div className="toast-detail">{toast.detail}</div>}
      </div>
      <button type="button" className="toast-dismiss" onClick={onDismiss} aria-label="Dismiss">
        <IconX size={14} />
      </button>
    </div>
  );
}
